import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AppContext } from "../../../context/AppContext";
import "../Announcement/Announcement.scss";
import api from "../../../api.js";
import { Calendar } from "primereact/calendar";
import Loader from "../../../components/Loader/Loader.jsx";

const OmbudsmanPage = () => {
  const { backendUrl, updateOmbudsmanGrievance } = useContext(AppContext);
  const [allOmbudsmanGrievances, setAllOmbudsmanGrievances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchAllOmbudsmanGrievances = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllOmbudsmanGrievances`
      );
      if (data.success) {
        setAllOmbudsmanGrievances(data.data);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchAllOmbudsmanGrievances();
  }, [fetchAllOmbudsmanGrievances, updateOmbudsmanGrievance]);

  const formatToDDMMYYYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownload = async () => {
    setIsSubmitting(true);
    if (!startDate || !endDate) {
      toast.warn("Please select both start and end dates.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be after end date.");
      return;
    }
    try {
      setLoading(true);
      const formattedStart = formatToDDMMYYYY(startDate);
      const formattedEnd = formatToDDMMYYYY(endDate);
      const response = await api.get(
        `${backendUrl}/api/auth/exportGrievancesExcel`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Grievances_${formattedStart}_to_${formattedEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download Excel file.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div className="contentSubParent">
              <h3>Do you really want to delete {rowData.name}'s Grievance?</h3>
              <p>Grievance: {rowData.grievance.substring(0, 100)}...</p>
              <p className="warningText">This action cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteOmbudsmanGrievance/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllOmbudsmanGrievances();
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const toggleStatus = async (rowData) => {
    setIsSubmitting(true);
    const id = rowData.id;
    const newStatus = !rowData.resolved;

    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleGrievanceResolvedStatus/${id}`,
        { resolved: newStatus }
      );

      const updatedGrievance = response.data.data;

      const updatedList = allOmbudsmanGrievances.map((grievance) =>
        grievance.id === id ? updatedGrievance : grievance
      );
      fetchAllOmbudsmanGrievances();
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="adminAnnoucementMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Admin Ombudsman Grievances Page</h2>
      </div>

      <div className="dateAndDownloadBtnContainer">
        <div className="dateFieldParent">
          <label htmlFor="startDate" className="dateLabelNames">
            Start Date
          </label>
          <Calendar
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="dateFieldParent">
          <label htmlFor="endDate" className="dateLabelNames">
            End Date
          </label>
          <Calendar
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="downloadExcelBtnParent">
          <Button
            label=" Download Excel"
            icon="pi pi-download"
            className="p-button-success"
            onClick={handleDownload}
            disabled={loading}
            style={{
              padding: "0.6rem 1.5rem",
              fontWeight: "600",
              fontSize: "1rem",
              fontFamily: "var(--font-paragraph)",
              gap: "0.5rem",
            }}
          />
        </div>
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allOmbudsmanGrievances}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No grievance to show"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="name"
              header="Name"
              className="tableData"
              style={{ minWidth: "210px", maxWidth: "325px" }}
            />
            <Column
              field="department"
              header="Department"
              className="tableData"
              style={{ whiteSpace: "normal" }}
              headerStyle={{ whiteSpace: "normal" }}
            />
            <Column
              field="year"
              header="Year"
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="contactNumber"
              header="Contact"
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="email"
              header="Email"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="grievance"
              header="Grievance"
              className="tableData"
              style={{ minWidth: "260px", maxWidth: "325px" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
            />
            <Column
              field="resolved"
              header="Resolved"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              bodyStyle={{ textAlign: "center" }}
              className="tableData"
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.resolved ? "Yes" : "No"}
                    severity={rowData.resolved ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleStatus(rowData)}
                  />
                </div>
              )}
              frozen
              alignFrozen="right"
            />
            <Column
              header="Action"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
              frozen
              alignFrozen="right"
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default OmbudsmanPage;
